/**
 * Number formatting for the dashboard.
 *
 * Display values are compacted (12.9K, £1.2M) so tiles and rows stay scannable and
 * never reflow when a figure gains a digit. The exact value is always one hover away
 * — compaction is a display convenience, never a way of hiding precision.
 */

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Attaches the currency symbol inside the sign, so a negative reads "-£52,300.00"
 * rather than "£-52,300.00". Write-offs and chargeback adjustments go negative here.
 */
const withCurrency = (formatted: string, n: number, currency: boolean): string => {
  if (!currency) return formatted;
  return n < 0 ? `-£${formatted.replace("-", "")}` : `£${formatted}`;
};

/** Full precision with thousands separators — what the tooltip shows. */
export function formatExact(value: unknown, currency = false): string {
  const n = toNumber(value);
  if (n === null) return currency ? "£0.00" : "0";

  const body = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: currency ? 2 : 0,
    maximumFractionDigits: currency ? 2 : 0,
  }).format(n);

  return withCurrency(body, n, currency);
}

/**
 * Compact display form.
 *
 * Below 10,000 stays written out with separators — "1,284" is just as scannable as
 * "1.3K" and loses nothing. At or above 10,000 it compacts to one decimal, so
 * 10,300 reads as "10.3K" and 1,240,000 as "1.2M".
 */
export function formatCompact(value: unknown, currency = false): string {
  const n = toNumber(value);
  if (n === null) return currency ? "£0" : "0";

  const abs = Math.abs(n);

  if (abs < 10_000) {
    const body = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: 0,
      maximumFractionDigits: currency ? 2 : 0,
    }).format(n);
    return withCurrency(body, n, currency);
  }

  const body = new Intl.NumberFormat("en-GB", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  })
    .format(n)
    // Intl renders "10.3K"/"1.2M" lowercase-free already, but normalise the
    // thousands marker so K/M/B are visually consistent across locales.
    .replace(/([km b])$/i, (s) => s.toUpperCase());

  return withCurrency(body, n, currency);
}

/** True when compaction actually changed the string — i.e. a tooltip adds information. */
export function isCompacted(value: unknown, currency = false): boolean {
  return formatCompact(value, currency) !== formatExact(value, currency);
}

/** Percentage helper used by the meters; guards the divide-by-zero cases. */
export function safePercent(part: unknown, whole: unknown): number {
  const p = toNumber(part) ?? 0;
  const w = toNumber(whole) ?? 0;
  if (!w) return 0;
  return Math.min(100, Math.max(0, (p / w) * 100));
}
